import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody } from './ui/Panel';
import Pill from './ui/Pill';
import { ErrorNotice, SkeletonRows } from './ui/States';
import { label } from '../lib/programs';
import { KIND_LABEL, STATUS_TONE, dayLabel, Needs } from '../lib/events';

interface Row {
  eventId: string;
  title: string;
  startsAt: string | null;
  kind: string | null;
  dayCount?: number | null;
  audience: string | null;
  role: string | null;
  status: string;
  days: string[];
  imported?: boolean;
  needs?: Needs;
  flags?: number;
}

const yearOf = (e: Row) => (e.startsAt ? new Date(e.startsAt).getFullYear() : null);

/** In-person events of a member as a timeline: invitations, status, days attended, role. */
export default function MemberEvents({ userId }: { userId: string }) {
  const list = useQuery<{ events: Row[] }>(
    ['person-events', userId],
    async () => (await api.get('/events/person', { params: { userId } })).data.data,
  );
  const events = list.data?.events ?? [];
  const came = events.filter((e) => e.days.length || e.status === 'attended');
  const years = came.map(yearOf).filter((y): y is number => y !== null);
  return (
    <Panel>
      <PanelHeader
        title="In-person events"
        note={came.length ? `attended ${came.length} of ${events.length}` : undefined}
      />
      <PanelBody className="pt-2">
        {list.isLoading ? (
          <SkeletonRows rows={2} />
        ) : list.error ? (
          <ErrorNotice>Could not load events.</ErrorNotice>
        ) : !events.length ? (
          <p className="text-small text-ink-2">No in-person event recorded.</p>
        ) : (
          <>
            {years.length ? (
              <p className="text-small text-ink-2 mb-2">
                {years.length === 1
                  ? `Came in ${years[0]}.`
                  : `Came ${came.length} times, first in ${Math.min(...years)}, last in ${Math.max(...years)}.`}
              </p>
            ) : null}
            <ol className="relative border-l border-line ml-[5px]">
              {events.map((e) => {
                const attended = e.days.length > 0 || e.status === 'attended';
                return (
                  <li key={e.eventId} className="pl-4 py-2 relative">
                    <span
                      className={`absolute -left-[7px] top-[14px] w-3 h-3 border border-line-strong ${attended ? 'bg-accent' : 'bg-white'}`}
                    />
                    <div className="flex items-start justify-between gap-3 text-table">
                      <div>
                        <div className="text-small text-ink-2">
                          {yearOf(e) ?? ''}
                          {e.kind ? ` · ${KIND_LABEL[e.kind] ?? e.kind}` : ''}
                        </div>
                        {e.imported ? (
                          <div>{e.title}</div>
                        ) : (
                          <Link className="underline" to={`/events/${e.eventId}`}>
                            {e.title}
                          </Link>
                        )}
                        <div className="text-small text-ink-2">
                          {e.role ?? (e.audience || !e.imported ? null : 'Imported attendance')}
                          {e.audience ? `${e.role ? ' · ' : ''}${e.audience}` : ''}
                          {e.days.length
                            ? ` · ${e.days.length}${e.dayCount ? ` of ${e.dayCount}` : ''} day${e.days.length === 1 && !e.dayCount ? '' : 's'}: ${e.days.map((d) => dayLabel(d)).join(', ')}`
                            : ''}
                          {e.flags ? ` · ${e.flags} flag${e.flags === 1 ? '' : 's'}` : ''}
                        </div>
                      </div>
                      <Pill tone={STATUS_TONE[e.status] ?? 'planned'}>{label(e.status)}</Pill>
                    </div>
                  </li>
                );
              })}
            </ol>
          </>
        )}
        <Link className="text-small underline block mt-3" to="/events">
          Events
        </Link>
      </PanelBody>
    </Panel>
  );
}
